import {
  HStack,
  Text,
  IconButton,
  Stack,
  Spacer,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useConst,
  Spinner,
  Input
} from '@chakra-ui/react';
import { useState } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import {
  HorizontalDotMenuIcon,
  SFArchiveFilled,
  SFArchiveOutlined,
  SFChatBubbleIcon,
  SFClockArrow2CirclesIcon,
  SFSquareAndPencil
} from '@assets/icons';
import { useStyledToast } from '@util/hooks';
import { formatTimeDistance } from '@util/helpers';
import { useChatStore } from '@store/chat/chat.store';
import {
  usePatchConversation,
  useSetConversationArchived
} from '@services/query-services/conversations.query-service';

const ChatDetailsBar = ({ conversation }) => {
  const now = useConst(new Date().getTime());
  const navigate = useNavigate();
  const toast = useStyledToast();
  const { mutateAsync: setConversationArchived, isLoading: isConversationBeingArchived } =
    useSetConversationArchived();
  const { mutate: patchConversation } = usePatchConversation();
  const setConversation = useChatStore((state) => state.setConversation);
  const setActiveChainOfThought = useChatStore((state) => state.setActiveChainOfThought);
  const setDisplayMessages = useChatStore((state) => state.setDisplayMessages);
  const title = useChatStore((state) => state.title);
  const setTitle = useChatStore((state) => state.setTitle);
  const queryClient = useQueryClient();
  const [isEditingTitle, setIsEditingTitle] = useState(false);

  const startOver = () => {
    const conversationId = conversation?.id;
    const originalSources = conversation?.messages[0]?.sources;
    setConversation({
      messages: [],
      artifacts: [],
      sources: [...originalSources]
    });
    setActiveChainOfThought([]);
    setDisplayMessages([]);
    setTitle(null);
    queryClient.invalidateQueries({
      queryKey: ['conversations', { id: conversationId }]
    });
    toast({ title: 'Starting a new chat with same sources.', status: 'info' });
    navigate('/explorer');
  };

  const archiveConversation = async () => {
    await setConversationArchived({ id: conversation?.id, value: !conversation?.archived_at });
    toast({ title: 'Conversation was successfully archived!', status: 'success' });
    navigate('/conversations-history?tags=type-card-explorer');
  };

  const archiveIcon = conversation?.archived_at ? (
    <SFArchiveFilled
      boxSize={4}
      mx={1}
    />
  ) : (
    <SFArchiveOutlined
      boxSize={4}
      mx={1}
    />
  );

  return (
    <HStack
      top={0}
      position='sticky'
      rounded='xl'
      px={6}
      py={3}
      bgColor='brandPrimary.primary'
      color='brandPrimary.onPrimary'
      zIndex='docked'
    >
      <SFChatBubbleIcon alignSelf='start' />
      <Stack gap={1}>
        {isEditingTitle ? (
          <Input
            data-testid='conversation-title-input'
            /* eslint-disable */
            autoFocus
            textStyle='body-600-lg'
            variant='flushed'
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={(e) => {
              setIsEditingTitle(false);
              patchConversation({ id: conversation?.id, title: e.target.value });
            }}
          />
        ) : (
          <Text
            data-testid='conversation-title'
            variant='body-600-lg'
            noOfLines={1}
            onClick={() => setIsEditingTitle(true)}
          >
            {title ?? 'New Conversation'}
          </Text>
        )}
        <HStack gap={3}>
          <Text
            data-testid='conversation-created-at'
            variant='label-reg-lg'
            noOfLines={1}
          >
            Started {format(conversation?.created_at ?? now, 'ccc, LLL d, yyyy h:mm a')}
          </Text>
          <Text
            data-testid='conversation-updated-at'
            variant='label-reg-lg'
            noOfLines={1}
          >
            Updated {formatTimeDistance(conversation?.updated_at ?? now, 'long')}
          </Text>
        </HStack>
      </Stack>
      <Spacer />
      <Menu>
        <MenuButton
          as={IconButton}
          variant='reskinWhite'
          px={3}
          size='sm'
          icon={<HorizontalDotMenuIcon />}
        />
        <MenuList>
          <MenuItem
            icon={
              <SFSquareAndPencil
                boxSize={4}
                mx={1}
              />
            }
            onClick={() => setIsEditingTitle(true)}
          >
            Rename Chat
          </MenuItem>
          <MenuItem
            icon={<SFClockArrow2CirclesIcon boxSize={6} />}
            onClick={startOver}
          >
            Start Over
          </MenuItem>
          <MenuItem
            onClick={archiveConversation}
            icon={isConversationBeingArchived ? <Spinner size='sm' /> : archiveIcon}
          >
            {conversation?.archived_at ? 'Unarchive' : 'Archive'}
          </MenuItem>
        </MenuList>
      </Menu>
    </HStack>
  );
};

export default ChatDetailsBar;
